import { useState, useEffect } from 'react';
import { purchaseAdminAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Check, X, Clock, ShoppingCart, ChevronLeft, ChevronRight, Eye, Image as ImageIcon, Building2, User, Banknote, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { formatPrice } from '@/lib/utils';

export default function PurchaseManagementTab() {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, purchase: null, action: null });
  const [detailDialog, setDetailDialog] = useState({ open: false, purchase: null });

  const itemsPerPage = 10;

  useEffect(() => {
    loadPurchases();
  }, [currentPage, statusFilter]);

  const loadPurchases = async () => {
    try {
      setLoading(true);
      const params = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage
      };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      const res = await purchaseAdminAPI.getAll(params);
      setPurchases(res.data.purchases || []);
      setTotalCount(res.data.total || 0);
    } catch (error) {
      toast.error('Gagal memuat data pembelian');
    } finally {
      setLoading(false);
    }
  };

  const sendWhatsAppNotification = (user, itemName, price, isConfirmed) => {
    if (!user?.phone) {
      toast.info('User tidak memiliki nomor telepon');
      return;
    }
    
    // Format phone number (remove leading 0, add 62)
    let phone = user.phone.replace(/\D/g, '');
    if (phone.startsWith('0')) {
      phone = '62' + phone.substring(1);
    } else if (!phone.startsWith('62')) {
      phone = '62' + phone;
    }
    
    const message = isConfirmed 
      ? `Halo ${user.name},\n\nPembayaran Anda untuk *${itemName}* sebesar ${formatPrice(price)} telah *DIKONFIRMASI*.\n\nSilakan login ke MyTentor untuk mengakses konten Anda.\n\nTerima kasih telah menggunakan MyTentor!`
      : `Halo ${user.name},\n\nMohon maaf, pembayaran Anda untuk *${itemName}* sebesar ${formatPrice(price)} *TIDAK DAPAT DIVERIFIKASI*.\n\nSilakan hubungi admin jika ada pertanyaan.\n\nTerima kasih.`;
    
    const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(waUrl, '_blank');
  };

  const handleConfirm = async () => {
    if (!confirmDialog.purchase) return;
    
    try {
      if (confirmDialog.action === 'confirm') {
        const res = await purchaseAdminAPI.confirm(confirmDialog.purchase.id);
        toast.success('Pembelian berhasil dikonfirmasi');
        
        // Open WhatsApp notification
        if (res.data.user) {
          sendWhatsAppNotification(res.data.user, res.data.item_name, res.data.price, true);
        }
      } else {
        const res = await purchaseAdminAPI.reject(confirmDialog.purchase.id);
        toast.success('Pembelian ditolak');
        
        // Open WhatsApp notification
        if (res.data.user) {
          sendWhatsAppNotification(res.data.user, res.data.item_name, res.data.price, false);
        }
      }
      setConfirmDialog({ open: false, purchase: null, action: null });
      loadPurchases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memproses');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'success':
        return <Badge className="bg-green-100 text-green-700"><Check className="w-3 h-3 mr-1" /> Dikonfirmasi</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><X className="w-3 h-3 mr-1" /> Ditolak</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Konfirmasi Pembelian</h1>
          <p className="text-slate-500 mt-1">Kelola permintaan pembelian dari user</p>
        </div>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="w-48">
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Status</SelectItem>
                  <SelectItem value="pending">Menunggu Konfirmasi</SelectItem>
                  <SelectItem value="success">Dikonfirmasi</SelectItem>
                  <SelectItem value="rejected">Ditolak</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="text-sm text-slate-500">
              Total: <span className="font-medium text-slate-900">{totalCount}</span> pembelian
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Purchases List - Compact Table */}
      <Card>
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm flex items-center gap-2">
            <ShoppingCart className="w-4 h-4" />
            Daftar Pembelian
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-6">
              <div className="w-6 h-6 border-4 border-[#4472C4] border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="mt-2 text-xs text-slate-500">Memuat...</p>
            </div>
          ) : purchases.length === 0 ? (
            <div className="text-center py-6 text-slate-500">
              <ShoppingCart className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-xs">Tidak ada data pembelian</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-[#4472C4] text-white">
                  <tr>
                    <th className="px-2 py-2 text-left">#</th>
                    <th className="px-2 py-2 text-left min-w-[120px]">User</th>
                    <th className="px-2 py-2 text-left min-w-[150px]">Item</th>
                    <th className="px-2 py-2 text-left">Tipe</th>
                    <th className="px-2 py-2 text-right">Harga</th>
                    <th className="px-2 py-2 text-left">Metode</th>
                    <th className="px-2 py-2 text-left min-w-[100px]">Tanggal</th>
                    <th className="px-2 py-2 text-center">Status</th>
                    <th className="px-2 py-2 text-center">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {purchases.map((purchase, idx) => (
                    <tr key={purchase.id} className="hover:bg-slate-50">
                      <td className="px-2 py-2 text-slate-500">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                      <td className="px-2 py-2">
                        <div className="font-medium truncate max-w-[120px]" title={purchase.user_name}>{purchase.user_name}</div>
                        <div className="text-[10px] text-slate-500 truncate max-w-[120px]" title={purchase.user_email}>{purchase.user_email}</div>
                      </td>
                      <td className="px-2 py-2">
                        <span className="font-medium truncate block max-w-[150px]" title={purchase.item_name}>{purchase.item_name}</span>
                      </td>
                      <td className="px-2 py-2">
                        <Badge variant="outline" className="text-[10px] px-1 py-0">
                          {purchase.item_type === 'tryout' ? 'TO' : 'Paket'}
                        </Badge>
                      </td>
                      <td className="px-2 py-2 text-right font-medium text-green-600 whitespace-nowrap">
                        {formatPrice(purchase.amount)}
                      </td>
                      <td className="px-2 py-2">
                        <Badge variant="outline" className={`text-[10px] px-1 py-0 whitespace-nowrap ${
                          purchase.payment_method === 'manual_transfer' 
                            ? 'bg-amber-50 text-amber-700 border-amber-200' 
                            : 'bg-blue-50 text-blue-700 border-blue-200'
                        }`}>
                          {purchase.payment_method === 'manual_transfer' ? 'Transfer' : 'Midtrans'}
                        </Badge>
                      </td>
                      <td className="px-2 py-2 text-slate-500 whitespace-nowrap">
                        {new Date(purchase.created_at).toLocaleDateString('id-ID', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="px-2 py-2 text-center">
                        {getStatusBadge(purchase.status)}
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center justify-center gap-1">
                          {/* View Detail Button */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDetailDialog({ open: true, purchase })}
                            className="text-slate-600 h-6 w-6 p-0"
                            title="Lihat Detail"
                          >
                            <Eye className="w-3 h-3" />
                          </Button>
                          
                          {purchase.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => setConfirmDialog({ open: true, purchase, action: 'confirm' })}
                                className="bg-green-600 hover:bg-green-700 h-6 w-6 p-0"
                                title="Konfirmasi"
                              >
                                <Check className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => setConfirmDialog({ open: true, purchase, action: 'reject' })}
                                className="h-6 w-6 p-0"
                                title="Tolak"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 p-4 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                First
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <span className="px-4 text-sm">
                {currentPage} / {totalPages}
              </span>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                Last
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ open, purchase: null, action: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDialog.action === 'confirm' ? 'Konfirmasi Pembelian?' : 'Tolak Pembelian?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.action === 'confirm' ? (
                <>
                  Anda akan mengkonfirmasi pembelian <strong>{confirmDialog.purchase?.item_name}</strong> oleh <strong>{confirmDialog.purchase?.user_name}</strong>.
                  User akan mendapatkan akses ke item ini.
                </>
              ) : (
                <>
                  Anda akan menolak pembelian <strong>{confirmDialog.purchase?.item_name}</strong> oleh <strong>{confirmDialog.purchase?.user_name}</strong>.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirm}
              className={confirmDialog.action === 'confirm' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-500 hover:bg-red-600'}
            >
              {confirmDialog.action === 'confirm' ? 'Konfirmasi' : 'Tolak'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Purchase Detail Dialog - 75% Compact */}
      <Dialog open={detailDialog.open} onOpenChange={(open) => setDetailDialog({ open, purchase: null })}>
        <DialogContent className="max-w-sm">
          <DialogHeader className="pb-1">
            <DialogTitle className="text-sm flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-[#4472C4]" />
              Detail Pembelian
            </DialogTitle>
          </DialogHeader>

          {detailDialog.purchase && (
            <div className="space-y-2 text-xs">
              {/* Status */}
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Status</span>
                {getStatusBadge(detailDialog.purchase.status)}
              </div>

              {/* User Info */}
              <div className="bg-slate-50 p-2 rounded space-y-1">
                <div className="flex items-center gap-1 text-slate-700 font-medium text-[10px]">
                  <User className="w-3 h-3" />
                  <span>Pembeli</span>
                </div>
                <p className="font-medium">{detailDialog.purchase.user_name}</p>
                <p className="text-slate-500 text-[10px]">{detailDialog.purchase.user_email}</p>
              </div>

              {/* Item Info */}
              <div className="bg-blue-50 p-2 rounded space-y-1">
                <div className="flex items-center gap-1 text-blue-700 font-medium text-[10px]">
                  <ShoppingCart className="w-3 h-3" />
                  <span>Item</span>
                </div>
                <p className="font-medium">{detailDialog.purchase.item_name}</p>
                <div className="flex justify-between text-[10px]">
                  <span className="text-slate-500">{detailDialog.purchase.item_type === 'tryout' ? 'Try Out' : 'Paket'}</span>
                  <span className="font-bold text-green-600">{formatPrice(detailDialog.purchase.amount)}</span>
                </div>
              </div>

              {/* Transfer Info */}
              {detailDialog.purchase.payment_method === 'manual_transfer' && (
                <div className="bg-amber-50 p-2 rounded space-y-1">
                  <div className="flex items-center gap-1 text-amber-700 font-medium text-[10px]">
                    <Building2 className="w-3 h-3" />
                    <span>Transfer</span>
                  </div>
                  {detailDialog.purchase.bank_name && (
                    <p><span className="text-slate-500">Bank:</span> {detailDialog.purchase.bank_name}</p>
                  )}
                  {detailDialog.purchase.sender_name && (
                    <p><span className="text-slate-500">Pengirim:</span> {detailDialog.purchase.sender_name}</p>
                  )}
                  {detailDialog.purchase.transfer_amount && (
                    <p><span className="text-slate-500">Nominal:</span> {formatPrice(detailDialog.purchase.transfer_amount)}</p>
                  )}
                  {detailDialog.purchase.notes && (
                    <p className="text-[10px] text-slate-600 pt-1 border-t border-amber-200">{detailDialog.purchase.notes}</p>
                  )}
                </div>
              )}

              {/* Payment Proof */}
              {detailDialog.purchase.payment_proof && (
                <div className="bg-green-50 p-2 rounded">
                  <div className="flex items-center gap-1 text-green-700 font-medium text-[10px] mb-1">
                    <ImageIcon className="w-3 h-3" />
                    <span>Bukti</span>
                  </div>
                  <a href={detailDialog.purchase.payment_proof} target="_blank" rel="noopener noreferrer" className="block">
                    <img src={detailDialog.purchase.payment_proof} alt="Bukti" className="max-h-32 rounded border border-green-200 mx-auto hover:opacity-90" />
                  </a>
                </div>
              )}

              {/* Timestamps */}
              <div className="text-[10px] text-slate-500 pt-1 border-t space-y-0.5">
                <p>Dibuat: {new Date(detailDialog.purchase.created_at).toLocaleString('id-ID')}</p>
                {detailDialog.purchase.confirmed_at && (
                  <p>{detailDialog.purchase.status === 'success' ? 'Dikonfirmasi' : 'Ditolak'}: {new Date(detailDialog.purchase.confirmed_at).toLocaleString('id-ID')}</p>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="gap-1 pt-1">
            {detailDialog.purchase?.status === 'pending' ? (
              <>
                <Button variant="outline" size="sm" className="h-6 text-xs border-red-300 text-red-600"
                  onClick={() => { setDetailDialog({ open: false, purchase: null }); setConfirmDialog({ open: true, purchase: detailDialog.purchase, action: 'reject' }); }}
                >
                  <X className="w-3 h-3 mr-1" />Tolak
                </Button>
                <Button size="sm" className="h-6 text-xs bg-green-600 hover:bg-green-700"
                  onClick={() => { setDetailDialog({ open: false, purchase: null }); setConfirmDialog({ open: true, purchase: detailDialog.purchase, action: 'confirm' }); }}
                >
                  <Check className="w-3 h-3 mr-1" />Konfirmasi
                </Button>
              </>
            ) : (
              <Button variant="outline" size="sm" className="h-6 text-xs" onClick={() => setDetailDialog({ open: false, purchase: null })}>
                Tutup
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
